import { useMemo } from 'react';
import { ArrowUpRight, Shapes } from 'lucide-react';
import { Link } from 'react-router';

import { Skeleton } from '@/components/ui/skeleton';
import {
  getFileIcon,
  getFileIconBackgroundColor,
} from '@/constants/node-styling';
import { ROUTES } from '@/constants/routes';
import { useListArtifacts } from '@/hooks/queries/artifacts/use-list-artifacts';
import type { ArtifactListItem } from '@/types/artifacts';
import { artifactTypeLabel } from '@/utils/artifact-type-label';
import { formatTimeAgo } from '@/utils/format-date';
import { orgPath } from '@/utils/org-path';

const MAX_ROWS = 5;

function nodeResultIdOf(item: ArtifactListItem): string | undefined {
  const value = (item as ArtifactListItem & { source_node_result_id?: string })
    .source_node_result_id;
  return typeof value === 'string' && value ? value : undefined;
}

export function filterArtifactsForNode(
  items: ArtifactListItem[],
  nodeId: string,
): ArtifactListItem[] {
  const hasNodeField = items.some((item) => nodeResultIdOf(item) !== undefined);
  if (!hasNodeField) return items;
  return items.filter((item) => nodeResultIdOf(item) === nodeId);
}

interface NodeArtifactsSectionProps {
  workflowId: string;
  nodeId: string;
}

export const NodeArtifactsSection = ({
  workflowId,
  nodeId,
}: NodeArtifactsSectionProps) => {
  const { data, isLoading, isError } = useListArtifacts({
    workflow_id: workflowId,
    limit: 50,
  });

  const artifacts = useMemo(
    () => filterArtifactsForNode(data?.artifacts ?? [], nodeId),
    [data, nodeId],
  );

  if (isError) return null;

  return (
    <div className='border-t p-4'>
      <div className='flex items-center justify-between'>
        <span className='text-sm font-medium'>Artifacts</span>
        <Link
          to={orgPath(`${ROUTES.ARTIFACTS}?workflow=${workflowId}`)}
          className='inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground'
        >
          View all
          <ArrowUpRight className='size-3' />
        </Link>
      </div>

      {isLoading ? (
        <div className='mt-2 flex flex-col gap-2'>
          {[0, 1].map((i) => (
            <div key={i} className='flex items-center gap-2 p-2.5'>
              <Skeleton className='size-8 shrink-0 rounded-md' />
              <div className='flex min-w-0 flex-1 flex-col gap-1.5'>
                <Skeleton className='h-4 w-1/2' />
                <Skeleton className='h-3 w-3/4' />
              </div>
            </div>
          ))}
        </div>
      ) : artifacts.length === 0 ? (
        <div className='mt-2 flex items-center gap-2 rounded-md border-2 border-dashed p-3'>
          <div className='flex size-9 shrink-0 items-center justify-center rounded-md bg-accent'>
            <Shapes className='size-5 text-muted-foreground' />
          </div>
          <div className='min-w-0'>
            <p className='text-sm font-medium'>No artifacts yet</p>
            <p className='text-sm text-muted-foreground'>
              Artifacts produced by this workflow will appear here
            </p>
          </div>
        </div>
      ) : (
        <div className='mt-1 flex flex-col gap-1'>
          {artifacts.slice(0, MAX_ROWS).map((artifact) => {
            const fileName = artifact.primary_file_name ?? artifact.title;
            const FileIcon = getFileIcon(fileName);
            const accent = getFileIconBackgroundColor(fileName);
            return (
              <div
                key={artifact.id}
                className='flex items-center gap-2 rounded-md bg-accent p-2.5'
              >
                <div
                  className='flex size-8 shrink-0 items-center justify-center rounded-md border border-border'
                  style={{ backgroundColor: accent }}
                >
                  <FileIcon className='size-4 text-foreground' />
                </div>
                <div className='flex min-w-0 flex-1 flex-col'>
                  <span className='truncate text-sm font-medium'>
                    {artifact.title}
                  </span>
                  <span className='truncate text-xs text-muted-foreground'>
                    {artifactTypeLabel(artifact.type)} · Updated{' '}
                    {formatTimeAgo(artifact.updated_at)}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
